/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { ReactNode, useMemo } from 'react';
import { styled, SupersetClient, t } from '@superset-ui/core';
import rison from 'rison';
import { AsyncSelect } from 'src/components';
import Label from 'src/components/Label';

export type DatabaseObject = {
  id: number;
  database_name: string;
  backend?: string;
};

const LabelStyle = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-left: ${({ theme }) => theme.gridUnit - 2}px;

  .backend {
    flex-shrink: 0;
    overflow: visible;
  }

  .name {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const SelectLabel = ({
  backend,
  databaseName,
}: {
  backend?: string;
  databaseName: string;
}) => (
  <LabelStyle>
    <Label className="backend">{backend || ''}</Label>
    <span className="name" title={databaseName}>
      {databaseName}
    </span>
  </LabelStyle>
);

export type DatabaseSelectValue = DatabaseObject & {
  label: ReactNode;
  value: number;
  id: number;
};

export interface DatabaseAsyncSelectProps {
  allowClear?: boolean;
  disabled?: boolean;
  onChange: (database: DatabaseSelectValue | null) => void;
  placeholder?: string;
  value?: DatabaseSelectValue | null;
}

export function toDatabaseSelectValue(db: DatabaseObject): DatabaseSelectValue {
  return {
    ...db,
    label: <SelectLabel backend={db.backend} databaseName={db.database_name} />,
    value: db.id,
  };
}

export default function DatabaseAsyncSelect({
  allowClear = true,
  disabled = false,
  onChange,
  placeholder = t('Select database or type to search databases'),
  value,
}: DatabaseAsyncSelectProps) {
  const loadDatabases = useMemo(
    () =>
      async (
        search: string,
        page: number,
        pageSize: number,
      ): Promise<{
        data: DatabaseSelectValue[];
        totalCount: number;
      }> => {
        const queryParams = rison.encode({
          order_columns: 'database_name',
          order_direction: 'asc',
          page,
          page_size: pageSize,
          filters: [{ col: 'database_name', opr: 'ct', value: search }],
        });
        const endpoint = `/api/v1/database/?q=${queryParams}`;
        return SupersetClient.get({ endpoint }).then(({ json }) => {
          const { result, count } = json;
          const options = result.map((row: DatabaseObject) =>
            toDatabaseSelectValue(row),
          );

          return {
            data: options,
            totalCount: count ?? options.length,
          };
        });
      },
    [],
  );

  return (
    <AsyncSelect
      allowClear={allowClear}
      ariaLabel={placeholder}
      data-test="select-database"
      disabled={disabled}
      lazyLoading={false}
      onChange={(_, database) =>
        onChange((database as DatabaseSelectValue) ?? null)
      }
      optionFilterProps={['database_name', 'value']}
      options={loadDatabases}
      placeholder={placeholder}
      showSearch
      value={value ?? undefined}
    />
  );
}
